import {
  CreditCardDispatcher,
  creditCardActions
} from './global/creditCardsState';
import { UserDispatcher, userActions } from './global/userState';
import { NextRouter } from 'next/router';
import { ACCOUNT_ROUTE } from '../domain/routes';
import { EVENT } from '../gateways/tracker/events';
import { FacebookEmail, FacebookAccessToken } from '../domain/facebook';
import { GoogleAccessToken, GoogleEmail } from '../domain/google';
import { Option } from '../utils/option';
import { SIGNUP_TYPE } from '../domain/signup';
import { SetState } from '../utils/type';
import { WA_SERVICE } from '../domain/waService';
import { createUrl, urlToWaService } from '../utils/url';
import BasePresenter from './basePresenter';
import CookieTokenStorage from '../gateways/storage/cookieTokenStorage';
import env from '../utils/env';
import facebookInteractor, {
  FacebookConfirmEmailAlreadyRegisteredError
} from '../application/usecases/facebookInteractor';
import googleInteractor, {
  GoogleConfirmEmailAlreadyRegisteredError
} from '../application/usecases/googleInteractor';
import i18n from '../R/string/i18n';
import tracker from '../gateways/tracker/tracker';
import userInteractor from '../application/usecases/userInteractor';
import appleInteractor from '../application/usecases/appleInteractor';
import lineTokenStorage from '../gateways/storage/lineTokenStorage';
import { convertWaServiceToTrackerRegisteredVia } from '../utils/registeredVia';

export default abstract class SocialPresenter extends BasePresenter {
  constructor(
    readonly setIsLoading: SetState<boolean>,
    readonly setServerErrMsg: SetState<string>,
    readonly setGoogleEmail: SetState<GoogleEmail>,
    readonly setGoogleAccessToken: SetState<GoogleAccessToken>,
    readonly setGoogleModalError: SetState<string>,
    readonly setIsGoogleConfirmationModalOpen: SetState<boolean>,
    readonly setFacebookEmail: SetState<FacebookEmail>,
    readonly setFacebookAccessToken: SetState<FacebookAccessToken>,
    readonly setFacebookModalError: SetState<string>,
    readonly setIsFacebookConfirmationModalOpen: SetState<boolean>,
    readonly userDispatcher: UserDispatcher,
    readonly cardsDispatcher: CreditCardDispatcher,
    readonly router: Option<NextRouter>,
    readonly setAccountSettingModalOpen: SetState<boolean>,
    readonly setShouldRedirectTo: SetState<boolean>
  ) {
    super(router);
  }

  private signupTracking = async (event: EVENT) => {
    const service = urlToWaService(this.redirectUrl);
    await tracker.track(event, {
      registeredVia: convertWaServiceToTrackerRegisteredVia(service)
    });
  };

  // lineTokenが存在すれば consent 画面へ、なければ通常リダイレクト
  protected redirectAfterSignIn = () => {
    const token = lineTokenStorage.get();
    if (token) {
      this.redirectTo(
        createUrl(WA_SERVICE.ACCOUNT, ACCOUNT_ROUTE.LINE_CONSENT, false)
      );
    } else {
      this.redirect();
    }
  };

  onAppleLoginSuccess = async (authCode: string) => {
    this.setIsLoading(true);
    const resultOrError = await appleInteractor.registerWith(
      new CookieTokenStorage()
    )(authCode, this.waService);

    resultOrError.fold(
      ({ message }) => {
        this.setServerErrMsg(message);
        this.setIsLoading(false);
      },
      ({ sessionStatus }) => {
        if (sessionStatus === 'signUp') {
          const service = urlToWaService(this.redirectUrl);

          tracker.track(EVENT.USER_SIGNED_UP_WITH_APPLE, {
            registeredVia: convertWaServiceToTrackerRegisteredVia(service)
          });

          this.trackConversion();
          this.setAccountSettingModalOpen(true);
        } else {
          tracker.track(EVENT.USER_SIGNED_IN_WITH_APPLE);
          this.getUserIfRedirectToAccount();
          this.redirectAfterSignIn();
        }
        this.setIsLoading(false);
      }
    );
  };

  onAppleLoginFailure = async () => {
    this.setServerErrMsg(i18n.socialLoginUserDidntGrantedError());
  };

  onFacebookLoginButtonPressed = async () => {
    this.setIsLoading(true);
    const resultOrError = await facebookInteractor.registerWith(
      new CookieTokenStorage()
    )(this.waService);
    this.setIsLoading(false);

    resultOrError.fold(
      ({ message }) => {
        this.setServerErrMsg(message);
      },
      async (registerResult) => {
        switch (registerResult.sessionType) {
          case 'signin':
            tracker.track(EVENT.USER_SIGNED_IN_WITH_FACEBOOK).then(() => {
              this.redirectAfterSignIn();
            });
            this.getUserIfRedirectToAccount();
            return;
          case 'signup':
            this.preSignupTracking(EVENT.USER_PRE_SIGNED_UP_WITH_FACEBOOK);
            this.setFacebookEmail(registerResult.email.getOrElse(() => ''));
            this.setFacebookAccessToken(registerResult.facebookAccessToken);
            this.setIsFacebookConfirmationModalOpen(true);
            return;
          case 'waitCheckingEmail':
            const confirmationPageUrl = createUrl(
              WA_SERVICE.ACCOUNT,
              ACCOUNT_ROUTE.SIGN_UP_CONFIRMATION,
              false,
              {
                redirect_url: this.redirectUrl
              }
            );
            this.redirectTo(confirmationPageUrl);
            return;
        }
      }
    );
  };

  onRequestFacebookConfirmation = async (
    facebookEmail: FacebookEmail,
    facebookAccessToken: FacebookAccessToken
  ) => {
    this.setIsLoading(true);

    const { gaTrackingId } = await tracker.getIds();
    const redirectUrlWithQuery = createUrl(
      WA_SERVICE.ACCOUNT,
      ACCOUNT_ROUTE.SIGN_UP_COMPLETE,
      true,
      {
        ga_tracking_id: gaTrackingId,
        sign_up_type: SIGNUP_TYPE.FACEBOOK,
        redirect_url: this.redirectUrl
      }
    );

    const res = await facebookInteractor.confirm(
      facebookEmail,
      facebookAccessToken,
      redirectUrlWithQuery
    );
    this.setIsLoading(false);

    res.fold(
      (error) => {
        if (error instanceof FacebookConfirmEmailAlreadyRegisteredError) {
          const tokenStorage = new CookieTokenStorage();
          tokenStorage.removeAccessToken();
          tokenStorage.removeRefreshToken();

          this.setIsFacebookConfirmationModalOpen(false);
          this.setServerErrMsg(error.message);
        } else {
          this.setFacebookModalError(error.message);
        }
      },
      async ({ type }) => {
        this.trackConversion();
        if (type === 'emailSameAsFacebookMail') {
          this.getUserIfRedirectToAccount();
          await this.signupTracking(EVENT.USER_SIGNED_UP_WITH_FACEBOOK);
          // this.setAccountSettingModalOpen(true);
          this.redirect();
        } else {
          const url = createUrl(
            WA_SERVICE.ACCOUNT,
            ACCOUNT_ROUTE.SIGN_UP_CONFIRMATION,
            false,
            {
              redirect_url: this.redirectUrl,
              sign_up_type: SIGNUP_TYPE.FACEBOOK
            }
          );
          tracker.track(EVENT.USER_NEED_CONFIRATION_WITH_FACEBOOK);
          // this.setAccountSettingModalOpen(true);
          // this.setShouldRedirectTo(true);
          this.redirectTo(url);
        }
      }
    );
  };

  onRequestGoogleConfirmation = async (
    email: GoogleEmail,
    accessToken: GoogleAccessToken
  ) => {
    this.setIsLoading(true);
    const res = await googleInteractor.confirm(email, accessToken);
    this.setIsLoading(false);

    res.fold(
      async (error) => {
        if (error instanceof GoogleConfirmEmailAlreadyRegisteredError) {
          const tokenStorage = new CookieTokenStorage();
          tokenStorage.removeAccessToken();
          tokenStorage.removeRefreshToken();

          this.setIsGoogleConfirmationModalOpen(false);
          this.setServerErrMsg(error.message);
        } else {
          this.setGoogleModalError(error.message);
        }
      },
      async () => {
        // 'emailSameAsGmail'の場合も 'emailDifferentFromGmail'の場合もsignup終了とする(WAPIの secure_user.isConfirmRequired参照)
        this.trackConversion();
        await this.signupTracking(EVENT.USER_SIGNED_UP_WITH_GOOGLE);
        this.setAccountSettingModalOpen(true); // check
      }
    );
  };

  onGoogleLoginSuccess = async (res: any) => {
    this.setIsLoading(true);
    const response = await googleInteractor.register(new CookieTokenStorage())(
      res.access_token,
      this.waService
    );

    this.setIsLoading(false);
    response.fold(
      ({ message }) => {
        this.setServerErrMsg(message);
      },
      ({ sessionType, googleEmail, googleAccessToken }) => {
        if (sessionType === 'signup') {
          this.preSignupTracking(EVENT.USER_PRE_SIGNED_UP_WITH_GOOGLE);
          this.setIsGoogleConfirmationModalOpen(true);
          this.setGoogleEmail(googleEmail);
          this.setGoogleAccessToken(googleAccessToken);
        } else {
          tracker.track(EVENT.USER_SIGNED_IN_WITH_GOOGLE).then(() => {
            this.redirectAfterSignIn();
          });
          this.getUserIfRedirectToAccount();
        }
      }
    );
  };

  onGoogleLoginFailure = () => {
    this.setIsLoading(false);
    if (env.isDevelopment) return;
    this.setServerErrMsg(i18n.socialLoginUserDidntGrantedError());
  };

  onGoogleModalRequestClose = () => {
    this.setIsGoogleConfirmationModalOpen(false);
  };

  onAccountSettingSaved = () => {
    this.setAccountSettingModalOpen(false);
  };

  onRedirect = () => {
    this.getUserIfRedirectToAccount();
    this.redirect();
  };

  onRedirectTo = (signUpType?: string) => {
    const confirmationPageUrl = createUrl(
      WA_SERVICE.ACCOUNT,
      ACCOUNT_ROUTE.SIGN_UP_CONFIRMATION,
      false,
      signUpType
        ? {
            redirect_url: this.redirectUrl,
            sign_up_type: signUpType
          }
        : {
            redirect_url: this.redirectUrl
          }
    );
    this.redirectTo(confirmationPageUrl);
  };

  onFacebookModalRequestClose = () => {
    this.setIsFacebookConfirmationModalOpen(false);
  };

  getUserIfRedirectToAccount = async () => {
    // 直接アカウントウェブを利用している場合はuserInfoを取得しておく。
    if (this.waService === WA_SERVICE.ACCOUNT) {
      const userInfo = await userInteractor.getMyInfo();
      userInfo.forEach(({ user, cards }) => {
        this.userDispatcher(userActions.gainSession(user));
        this.cardsDispatcher(creditCardActions.replace(cards));
      });
    }
  };
}
